import { NavLink } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { NAV, SECTION_ORDER } from "@/constants/nav";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { ChevronLeft } from "lucide-react";
import api from "@/lib/api";

const FOUNDER_RA_OVERVIEW_PATHS = new Set(["/ra/team"]);
const HRMS_EMPLOYEE_ROLES = new Set([
  "member",
  "recruiter",
  "research_analyst",
  "bdm",
  "team_lead",
  "team_admin",
  "ra_admin",
  "recruitment_manager",
  "hr",
  "hr_admin",
  "finance",
  "payroll_admin",
  "vp_director",
  "administrator",
]);

export default function Sidebar({
  collapsed,
  onToggle,
  mobileOpen,
  onMobileClose,
}) {
  const { can, user } = useAuth();
  const [commsUnread, setCommsUnread] = useState(0);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/communications/unread-summary");
      setCommsUnread(Number(data?.total || 0));
    } catch {
      // Silent — badge is a nice-to-have.
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadUnread();
    const interval = setInterval(loadUnread, 60_000);
    const onFocus = () => loadUnread();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, loadUnread]);
  // Super Admin retains full visibility and access to every module.
  let items = NAV.filter(
    (i) =>
      (!i.perm || can(i.perm)) && (!i.roles || i.roles.includes(user?.role)),
  );
  if (
    HRMS_EMPLOYEE_ROLES.has(user?.role) &&
    !items.some((item) => item.path === "/hrms")
  ) {
    const hrmsItem = NAV.find((item) => item.path === "/hrms");
    if (hrmsItem) items.push(hrmsItem);
  }
  // Focused Research Analyst role: R.A. Team Desk only.
  if (user?.role === "research_analyst")
    items = items.filter(
      (i) => i.section === "R.A. Team Desk" || i.path === "/hrms",
    );
  // Founder allow-list: company R.A. achievement, never personal operational queues.
  if (user?.role === "founder") {
    items = items.filter(
      (item) =>
        item.section !== "R.A. Team Desk" ||
        FOUNDER_RA_OVERVIEW_PATHS.has(item.path),
    );
  }
  const bySection = SECTION_ORDER.map((s) => ({
    section: s,
    items: items.filter((i) => i.section === s),
  })).filter((g) => g.items.length);

  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "bg-sidebar border-r border-sidebar flex-col h-screen transition-all duration-300 z-30",
        mobileOpen
          ? "fixed inset-y-0 left-0 flex w-72 shadow-2xl md:sticky md:top-0 md:shadow-none"
          : "hidden md:sticky md:top-0 md:flex",
        collapsed && !mobileOpen ? "w-16" : "w-64",
      )}
    >
      <div className="h-16 flex items-center gap-2.5 px-4 border-b border-sidebar shrink-0">
        <div
          className={
            "h-8 w-8 rounded-lg bg-[#255058] text-white grid place-items-center " +
            "font-display font-bold text-sm shrink-0"
          }
        >
          N
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-display font-bold leading-tight tracking-tight">
              Nexreq
            </div>
            <div className="text-[10px] text-muted-foreground leading-tight truncate">
              Your People Partner
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2 space-y-4">
        {bySection.map((group) => (
          <div key={group.section}>
            {!collapsed && (
              <div
                className={
                  "px-2 mb-1 text-[10px] font-semibold uppercase tracking-wider " +
                  "text-muted-foreground"
                }
              >
                {group.section}
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const badgeCount = item.path === "/communications" ? commsUnread : 0;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onMobileClose}
                    end={item.path === "/"}
                    data-testid={`sidebar-nav-${item.label
                      .toLowerCase()
                      .replace(/[^a-z]+/g, "-")
                      .replace(/(^-|-$)/g, "")}`}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium " +
                          "transition-colors relative",
                        collapsed && "justify-center",
                        isActive
                          ? "bg-[#255058] text-white shadow-sm"
                          : "text-foreground/70 hover:bg-accent hover:text-foreground",
                      )
                    }
                  >
                    <span className="relative shrink-0">
                      <Icon className="h-[18px] w-[18px]" />
                      {badgeCount > 0 && collapsed && (
                        <span
                          data-testid={`sidebar-badge-collapsed-${item.label
                            .toLowerCase()
                            .replace(/[^a-z]+/g, "-")}`}
                          className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center leading-none"
                        >
                          {badgeCount > 99 ? "99+" : badgeCount}
                        </span>
                      )}
                    </span>
                    {!collapsed && (
                      <>
                        <span className="truncate flex-1">{item.label}</span>
                        {badgeCount > 0 && (
                          <span
                            data-testid={`sidebar-badge-${item.label
                              .toLowerCase()
                              .replace(/[^a-z]+/g, "-")}`}
                            className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center leading-none"
                          >
                            {badgeCount > 99 ? "99+" : badgeCount}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <button
        data-testid="sidebar-collapse-toggle"
        onClick={mobileOpen ? onMobileClose : onToggle}
        className={
          "h-11 border-t border-sidebar flex items-center justify-center gap-2 text-xs " +
          "text-muted-foreground hover:bg-accent hover:text-foreground " +
          "transition-colors shrink-0"
        }
      >
        <ChevronLeft
          className={cn(
            "h-4 w-4 transition-transform",
            collapsed && "rotate-180",
          )}
        />
        {!collapsed && (mobileOpen ? "Close" : "Collapse")}
      </button>
    </aside>
  );
}
